/**
 * Types and store for work toasts — one pill-shaped item per piece of work
 * (agent execution, native worker, approval request, or task).
 */

import { create, type StoreApi, type UseBoundStore } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { isTerminalState, isWorkToastVisible, TERMINAL_PRUNE_MS, workToastStatusConversation } from "@/components/toasts/toast-status";
import { isComputerWorkToastPinned } from "@/lib/work-toast-computer-pin";
import type { ActivityLabel } from "@/lib/activity-label";
import type { CliChoice } from "@/components/ui/cli-logo";

export type WorkToastState =
  | "working"
  | "question"
  | "approval"
  | "blocked"
  | "done"
  | "failed"
  | "stopped";

export type WorkToastSource = "agent" | "native_worker" | "approval" | "task" | "computer";

export interface WorkToastQuestionOption {
  id: string;
  label: string;
  description?: string;
  task?: { title: string; description: string };
}

export interface WorkToastQuestion {
  awaitingInputId: string;
  question: string;
  context: string | null;
  options: WorkToastQuestionOption[];
  index?: number;
  count?: number;
}

/** A coding session's tool approval; it answers through its task, until it
 *  expires. */
export interface WorkToastApproval {
  approvalId: string;
  title: string;
  description: string;
  expiresAt: number | null;
}

export interface WorkToastAppRef {
  appId: string;
  route: "ax" | "mcp" | "provider";
  confirmed: boolean;
  label?: string;
  iconUrl?: string | null;
  /** Place in the backend's app list, which is the brief's order, primary
   *  first. The list itself is ordered by touch; open targets follow this. */
  briefOrder?: number;
}

export interface WorkToastOpenTarget {
  appId: string;
  label: string;
  /** Stable target identity when supplied by newer servers. */
  id?: string;
  /** How Denker should present the destination. Legacy payloads omit this. */
  kind?: "resource" | "app";
  /** Route that produced the destination. */
  route?: WorkToastAppRef["route"];
  /** Canonical destination. `url` remains the backward-compatible wire name.
   *  Absent iff `kind === "app"` (url-less desktop-app targets). */
  uri?: string;
  url?: string;
  expectedBundleId?: string | null;
}

export interface WorkToastComputerActivity {
  appLabel: string | null;
  actionKey: string | null;
  /** The step in English; `actionActivityLabel` renders it in the app
   *  language at display time and changes with it. */
  actionLabel: string | null;
  actionActivityLabel?: ActivityLabel | null;
  fieldNames?: string[];
  updatedAt: number;
  active: boolean;
}

export interface WorkToastActivityEntry {
  id: string;
  kind: "computer" | "tool" | "text";
  app?: string;
  /** The step in English; `actionLabel` renders it in the app language at
   *  display time. */
  action?: string;
  actionLabel?: ActivityLabel;
  text?: string;
  /** Full assistant text for the expanded card; `text` remains its row preview. */
  textFull?: string;
  updatedAt: number;
}

export interface WorkToastComputerScreenshot {
  captureId: string;
  ordinal: number;
  displayId?: number | null;
  url: string;
}

export interface WorkToastItem {
  key: string; // execution_id | `approval:${id}` | `task:${id}`
  source: WorkToastSource;
  tracked: boolean;
  state: WorkToastState;
  stateBeforeBlocked: WorkToastState | null;
  agentName: string;
  projectId: string | null;
  conversationId: string | null;
  /** Display where the prompt was sent; retained across execution updates. */
  originDisplayId?: number | null;
  taskId: string | null;
  nativeSubagentId: string | null;
  apps?: WorkToastAppRef[];
  /** The coding app of the user's coding session (its tool approvals); null
   *  for Denker's own runs, whichever CLI runs them. */
  codingProvider?: CliChoice | null;
  openTargets?: WorkToastOpenTarget[];
  detail: string;
  resultText: string | null;
  /** Later live activity has replaced this result as the current progress. */
  resultSuperseded?: boolean;
  /** Displays of the latest screen capture, by ordinal; the next capture replaces them. */
  computerScreenshots?: WorkToastComputerScreenshot[];
  /** Latest computer-use activity (app / action) merged from desktop frames.
   *  Present on both direct computer toasts and sub-agent toasts that drove
   *  the screen; drives the live-cycle logo and the detail suffix. */
  computer?: WorkToastComputerActivity | null;
  /** Latest three events, oldest first. */
  activity?: WorkToastActivityEntry[];
  question: WorkToastQuestion | null;
  approval: WorkToastApproval | null;
  /** A saved reply has its own delivery lifecycle, separate from its execution. */
  answerDelivery?: {
    awaitingInputId: string;
    executionId: string;
    status: "pending" | "unavailable" | "delivered" | "superseded";
  };
  stateChangedAt: number; // epoch ms
  dismissed: boolean;
}

export interface OpenTaskRequest {
  projectId: string;
  taskId: string;
  requestedAt: number;
}

export interface WorkToastStoreState {
  items: Record<string, WorkToastItem>;
  expandedKey: string | null;
  expandedDisplayId: number | null;
  /** The user opened the expanded card (a click, a shortcut), so it may take
   *  the keyboard. A card Denker revealed on its own never does. */
  expandedByUser: boolean;
  hoveredKey: string | null;
  selectedComputerKey: string | null;
  openTaskRequest: OpenTaskRequest | null;
  upsertItem: (item: WorkToastItem) => void;
  patchItem: (key: string, patch: Partial<Omit<WorkToastItem, "key">>) => void; // no-op when absent
  removeItem: (key: string) => void; // also clears expandedKey/hoveredKey pointing at it
  dismiss: (key: string) => void; // sets dismissed; collapses if expanded
  expand: (key: string, displayId: number | null) => void; // the user opened it
  reveal: (key: string, displayId: number | null) => void; // shown by itself: no focus
  collapse: () => void;
  setHovered: (key: string | null) => void;
  selectComputer: (key: string) => void;
  requestOpenTask: (request: { projectId: string; taskId: string }) => void;
  consumeOpenTaskRequest: () => OpenTaskRequest | null; // returns and clears
  pruneTerminal: (now: number) => void; // retains the latest actionable conversation result
}

function withoutKeys(items: Record<string, WorkToastItem>, keys: string[]): Record<string, WorkToastItem> {
  const next = { ...items };
  for (const key of keys) delete next[key];
  return next;
}

function enforceLiveInvariants(item: WorkToastItem): WorkToastItem {
  const released = item.state !== "working" && item.computer?.active
    ? { ...item, computer: { ...item.computer, active: false } }
    : item;
  return isTerminalState(released.state) && released.activity?.length
    ? { ...released, activity: [] }
    : released;
}

/** One item per piece of work. Frames write through the adapter in every window
 *  (mutation emit suppressed); only UI actions cross windows as mutations. */
export const useWorkToastStore: UseBoundStore<StoreApi<WorkToastStoreState>> = create<WorkToastStoreState>()(
  mirroredStore("workToast", (set, get) => ({
    items: {},
    expandedKey: null,
    expandedDisplayId: null,
    expandedByUser: false,
    hoveredKey: null,
    selectedComputerKey: null,
    openTaskRequest: null,
    upsertItem: (item) => set((s) => ({ items: { ...s.items, [item.key]: item } })),
    patchItem: (key, patch) =>
      set((s) => {
        const current = s.items[key];
        return current ? { items: { ...s.items, [key]: enforceLiveInvariants({ ...current, ...patch }) } } : s;
      }),
    removeItem: (key) =>
      set((s) => {
        if (!s.items[key]) return s;
        const expanded = s.expandedKey === key;
        return {
          items: withoutKeys(s.items, [key]),
          expandedKey: expanded ? null : s.expandedKey,
          expandedDisplayId: expanded ? null : s.expandedDisplayId,
          hoveredKey: s.hoveredKey === key ? null : s.hoveredKey,
          selectedComputerKey: s.selectedComputerKey === key ? null : s.selectedComputerKey,
        };
      }),
    // The flag is cleared again when the item reaches a genuinely different state
    // (`moveWorkToast`, 1b Task 8), so later Question, Done or Failed pills still show.
    dismiss: (key) =>
      set((s) => {
        const current = s.items[key];
        if (!current) return s;
        const expanded = s.expandedKey === key;
        return {
          items: { ...s.items, [key]: { ...current, dismissed: true } },
          expandedKey: expanded ? null : s.expandedKey,
          expandedDisplayId: expanded ? null : s.expandedDisplayId,
          hoveredKey: s.hoveredKey === key ? null : s.hoveredKey,
          selectedComputerKey: s.selectedComputerKey === key ? null : s.selectedComputerKey,
        };
      }),
    // The pill unmounts under the pointer without a mouse-leave, so release its hover here.
    expand: (key, displayId) =>
      set((s) => (s.items[key] ? { expandedKey: key, expandedDisplayId: displayId, expandedByUser: true, hoveredKey: null } : s)),
    reveal: (key, displayId) =>
      set((s) => (s.items[key] ? { expandedKey: key, expandedDisplayId: displayId, expandedByUser: false, hoveredKey: null } : s)),
    collapse: () => set({ expandedKey: null, expandedDisplayId: null, hoveredKey: null }),
    setHovered: (key) => set((s) => (s.hoveredKey === key ? s : { hoveredKey: key })),
    selectComputer: (key) => set({ selectedComputerKey: key }),
    requestOpenTask: ({ projectId, taskId }) => set({ openTaskRequest: { projectId, taskId, requestedAt: Date.now() } }),
    consumeOpenTaskRequest: () => {
      const request = get().openTaskRequest;
      if (request) set({ openTaskRequest: null });
      return request;
    },
    pruneTerminal: (now) =>
      set((s) => {
        const latestByConversation = new Map<string, WorkToastItem>();
        for (const item of Object.values(s.items)) {
          const conversationId = workToastStatusConversation(item);
          if (!conversationId || item.answerDelivery) continue;
          const previous = latestByConversation.get(conversationId);
          if (!previous || item.stateChangedAt > previous.stateChangedAt) {
            latestByConversation.set(conversationId, item);
          }
        }
        const stale = Object.values(s.items)
          .filter((item) => {
            if (isComputerWorkToastPinned(item)) return false;
            if (!isTerminalState(item.state) || now - item.stateChangedAt < TERMINAL_PRUNE_MS) return false;
            const conversationId = workToastStatusConversation(item);
            const superseded = conversationId && latestByConversation.get(conversationId) !== item;
            if (item.state === "done" && conversationId && !item.dismissed && !superseded) return false;
            const ui = { hovered: s.hoveredKey === item.key, expanded: s.expandedKey === item.key };
            return !isWorkToastVisible(item, now, ui) || (superseded && !ui.hovered && !ui.expanded);
          })
          .map((item) => item.key);
        return stale.length > 0 ? { items: withoutKeys(s.items, stale) } : s;
      }),
  })),
);

export function findWorkToastKeyByAwaitingInputId(
  items: Record<string, WorkToastItem>,
  awaitingInputId: string,
): string | null {
  for (const item of Object.values(items)) {
    if (item.question?.awaitingInputId === awaitingInputId) return item.key;
  }
  return null;
}
